"use client";

import { useCallback } from "react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const iconBaseClass =
  "cursor-pointer inline-flex items-center justify-center text-current rounded focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background transition-all duration-200";

interface SocialLinkProps {
  href?: string;
  copyText?: string;
  className?: string;
  style?: React.CSSProperties;
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
}

/**
 * Renders a social link as either:
 * - A regular `<a>` tag (when `href` is provided) — opens in a new browser tab
 *   so the user sees the profile page and can choose to open in the app
 * - A `<button>` that copies text to clipboard with a toast notification (when `copyText` is provided)
 */
export function SocialLink({
  href,
  copyText,
  className,
  style,
  label,
  children,
  onClick,
}: SocialLinkProps) {
  const handleCopy = useCallback(async () => {
    if (!copyText) return;
    trackEvent("outbound_click", {
      platform: label,
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
    });
    try {
      await navigator.clipboard.writeText(copyText);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = copyText;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
    // Show the global toast (persists even if this component unmounts)
    toast.success("WeChat ID copied to clipboard!");
    // Fire onClick (e.g. to close mobile menu) — toast survives this
    onClick?.();
  }, [copyText, onClick, label]);

  const handleClick = useCallback(() => {
    trackEvent("outbound_click", {
      platform: label,
      page_path: typeof window !== "undefined" ? window.location.pathname : "",
    });
    onClick?.();
  }, [label, onClick]);

  if (copyText) {
    return (
      <button
        type="button"
        onClick={handleCopy}
        className={[iconBaseClass, className].filter(Boolean).join(" ")}
        style={style}
        aria-label={`Copy ${label} ID`}
        title={`Copy ${label} ID: ${copyText}`}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} (opens in new tab)`}
      onClick={handleClick}
      className={[iconBaseClass, className].filter(Boolean).join(" ")}
      style={style}
    >
      {children}
    </a>
  );
}
